/* eslint-disable camelcase */
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useSearchParams } from 'react-router-dom';
import FormProvider from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import { addToCart } from 'src/redux/slices/checkout';
import { useOrderingV2Ui } from 'src/contexts/ordering-v2-ui-context';
import { fNumber } from 'src/utils/format-number';
import placeholderSvg from 'src/new_designs/product-image-placeholder.svg';
import ProductModifierFields from './product-modifier-fields';

// ----------------------------------------------------------------------

function parseImage(img) {
  if (img && String(img).includes('http')) return img;
  if (img) return `https://syspos.s3.me-central-1.amazonaws.com/media/${img}`;
  return null;
}

export default function OrderV2ProductDetailModal() {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { detailProduct, closeProductDetail, menuLocation, openCart } = useOrderingV2Ui();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const brand_id = searchParams.get('brand_id');
  const [qty, setQty] = useState(1);

  const methods = useForm({
    resolver: yupResolver(Yup.object().shape({})),
    defaultValues: { selectedModifiers: [], selectedSingleModifier: {} },
  });

  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (detailProduct) {
      const selectedSingleModifier = {};
      detailProduct.modifierGroups?.forEach((mg) => {
        if (mg?.selection_allowed === 1) {
          selectedSingleModifier[`mg-${mg.id}`] = '';
        }
      });
      reset({ selectedModifiers: [], selectedSingleModifier });
      setQty(1);
    }
  }, [detailProduct, reset]);

  const currency = detailProduct?.currency || 'AED';
  const modifierGroups = detailProduct?.modifierGroups;

  const getPrice = (value) => {
    if (Number(value) === 0) return 'Free';
    return `${value} ${currency}`;
  };

  const onSubmit = handleSubmit((data) => {
    if (!menuLocation?.enable_menu_checkout) {
      enqueueSnackbar('Checkout is not enabled for this location', { variant: 'error' });
      return;
    }
    const product = detailProduct;
    const { id, name, image, price } = product;
    const available = 1000;

    const allModifiers = [];
    modifierGroups?.forEach((mg) => {
      allModifiers.push(...(mg?.modifiers || []));
    });
    const singleModifiers = Object.values(data.selectedSingleModifier || {}).filter(
      (v) => v !== '' && v != null
    );
    const groupModifiers = data.selectedModifiers || [];
    const selectedModifiers = [...singleModifiers, ...groupModifiers]
      .map((mId) => allModifiers?.find((am) => Number(mId) === Number(am?.id)))
      .filter(Boolean);

    const unitPrice =
      price +
      [...(selectedModifiers || [])].reduce((sum, item) => parseFloat(sum) + parseFloat(item?.price || 0), 0);

    const newProduct = {
      ...product,
      id,
      name,
      image,
      available,
      price: unitPrice,
      quantity: qty,
      selectedModifiers,
      brand_id,
    };

    for (let i = 0; i < qty; i += 1) {
      dispatch(addToCart({ ...newProduct, quantity: 1 }));
    }
    enqueueSnackbar(`${name} added to cart`, { variant: 'success' });
    closeProductDetail();
    openCart();
  });

  const handleQuickAdd = () => {
    if (!menuLocation?.enable_menu_checkout) {
      enqueueSnackbar('Checkout is not enabled for this location', { variant: 'error' });
      return;
    }
    if (modifierGroups?.length > 0) {
      return;
    }
    const { id, name, image, price } = detailProduct;
    const newProduct = {
      ...detailProduct,
      id,
      name,
      image,
      available: 1000,
      price,
      quantity: 1,
      brand_id,
    };
    for (let i = 0; i < qty; i += 1) {
      dispatch(addToCart({ ...newProduct, quantity: 1 }));
    }
    enqueueSnackbar(`${name} added to cart`, { variant: 'success' });
    closeProductDetail();
    openCart();
  };

  const imgSrc = useMemo(() => parseImage(detailProduct?.image), [detailProduct]);

  if (!detailProduct) {
    return null;
  }

  const basePrice = detailProduct.actual_price ?? detailProduct.price;
  const hasModifiers = modifierGroups?.length > 0;

  const stickyFooter = (
    <Box
      sx={{
        flexShrink: 0,
        pt: 2,
        mt: { xs: 0, md: 'auto' },
        borderTop: (t) => `1px solid ${t.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} spacing={2}>
        <Typography variant="h6" sx={{ color: '#0e7c62', fontWeight: 800 }}>
          {currency} {fNumber(basePrice)}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => setQty((q) => Math.max(1, q - 1))}
            aria-label="Decrease quantity"
            sx={{
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: 'background.paper',
            }}
          >
            <Iconify icon="eva:minus-fill" />
          </IconButton>
          <Typography sx={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>{qty}</Typography>
          <IconButton
            size="small"
            onClick={() => setQty((q) => q + 1)}
            aria-label="Increase quantity"
            sx={{
              border: (t) => `1px solid ${t.palette.divider}`,
              bgcolor: 'background.paper',
            }}
          >
            <Iconify icon="eva:plus-fill" />
          </IconButton>
        </Stack>
      </Stack>
      <Button
        type={hasModifiers ? 'submit' : 'button'}
        variant="contained"
        size="large"
        fullWidth
        onClick={hasModifiers ? undefined : handleQuickAdd}
        sx={{
          bgcolor: '#0e7c62',
          '&:hover': { bgcolor: '#065f4a' },
          fontWeight: 700,
          py: 1.25,
          borderRadius: 2,
        }}
      >
        Add to cart
      </Button>
    </Box>
  );

  const imageBlock = (
    <Box
      sx={{
        bgcolor: '#f5f5f7',
        borderRadius: 2,
        aspectRatio: { xs: '1 / 1', md: '1 / 1' },
        maxHeight: { md: 'min(100%, 420px)' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <Box
        component="img"
        src={imgSrc || placeholderSvg}
        alt=""
        sx={{ width: '90%', height: '90%', objectFit: 'contain' }}
      />
    </Box>
  );

  return (
    <Dialog
      open
      onClose={closeProductDetail}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      scroll="paper"
      disableScrollLock={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          height: fullScreen ? '100%' : 'auto',
          maxHeight: fullScreen ? '100dvh' : 'min(720px, 92vh)',
          display: 'flex',
          flexDirection: 'column',
          ...(fullScreen
            ? {
                // Scroll the sheet itself — nested overflow:auto often fails on iOS / mobile emulation
                overflowY: 'auto',
                overflowX: 'hidden',
                WebkitOverflowScrolling: 'touch',
                touchAction: 'pan-y',
                overscrollBehaviorY: 'contain',
              }
            : { overflow: 'hidden' }),
        },
      }}
    >
      <DialogTitle sx={{ pr: 6, flexShrink: 0 }}>
        <Typography variant="h6" component="span" fontWeight={700}>
          {detailProduct.name}
        </Typography>
        <IconButton aria-label="Close" onClick={closeProductDetail} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent
          dividers={false}
          sx={
            fullScreen
              ? {
                  // Override MUI defaults (flex:1 + overflowY:auto) so the Paper is the only scroll container
                  flex: '0 0 auto',
                  flexGrow: 0,
                  flexShrink: 0,
                  overflow: 'visible',
                  overflowY: 'visible',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  pt: 0,
                  px: 2,
                  pb: `max(16px, env(safe-area-inset-bottom, 0px))`,
                }
              : {
                  flex: 1,
                  minHeight: 0,
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 3,
                  pt: 0,
                  px: 3,
                  pb: 3,
                }
          }
        >
          {fullScreen ? (
            <>
              <Box sx={{ flexShrink: 0 }}>{imageBlock}</Box>
              {detailProduct.description && (
                <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {detailProduct.description}
                </Typography>
              )}
              <Box sx={{ flexShrink: 0 }}>
                <ProductModifierFields visual modifierGroups={modifierGroups} getPrice={getPrice} />
              </Box>
              {stickyFooter}
            </>
          ) : (
            <>
              <Box
                sx={{
                  flex: '0 0 42%',
                  width: '42%',
                  maxWidth: '42%',
                }}
              >
                {imageBlock}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  maxHeight: 'min(560px, calc(92vh - 180px))',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    pr: 0.5,
                  }}
                >
                  {detailProduct.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {detailProduct.description}
                    </Typography>
                  )}
                  <ProductModifierFields visual modifierGroups={modifierGroups} getPrice={getPrice} />
                </Box>
                {stickyFooter}
              </Box>
            </>
          )}
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
}
